import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { DataState } from '../../context/DataProvider';
import BaseLayout from '../../components/layout/BaseLayout';
import ApprovalsList from '../../components/project-register/ApprovalsList';
import CommitmentsList from '../../components/project-register/CommitmentsList';
import ConcernList from '../../components/project-register/ConcernList';
import HowThisWorksPanel from '../../components/project-register/HowThisWorksPanel';
import InsightsGantt from '../../components/project-register/InsightsGantt';
import MailTriageList from '../../components/project-register/MailTriageList';
import SlaList from '../../components/project-register/SlaList';
import { ANALYTICS_TABS } from '../../utils/analyticsTabs';

/**
 * Project Register: Deliverables, Gantt, Commitments, Concerns, and SLA views
 * rendered inside the unified BaseLayout app frame, matching /chat and /workflow.
 *
 * The section shown is driven by the URL (see ANALYTICS_TABS), not local
 * state — Sidebar renders the actual nav for switching between them, so
 * this page just reads which one is current off `location.pathname`.
 */
export default function ProjectRegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userGoogleId: projectToken } = DataState();
  const activeTab =
    ANALYTICS_TABS.find((t) => t.path === location.pathname)?.id ?? 'insights';
  // Owned here (not inside InsightsGantt) so they only need to exist while
  // Insights is active, without InsightsGantt needing to know about routing.
  const [ganttView, setGanttView] = useState('GANTT');
  const [ganttZoom, setGanttZoom] = useState('WEEK');
  // Fed by InsightsGantt's onStatsChange so this page can render the summary
  // next to the sidebar's collapse/expand button instead of down in the
  // Insights content itself.
  const [insightsStats, setInsightsStats] = useState(null);

  return (
    <BaseLayout
      tabBarAbsolute={true}
      tabName="analytics"
      createName="New Chat"
      handleCreate={() => navigate('/chat')}
    >
      {/* Aligned with the top tab bar (BaseLayout renders it `absolute top-2`) */}
      <div className="absolute right-2 top-2 z-20 hidden sm:block">
        <HowThisWorksPanel />
      </div>

      {/* left-14 clears BaseLayout's sidebar collapse/expand button, which
          sits at left-2 top-2, with room to read as its own element rather
          than crowding it. leading-7 (not a fixed height + flex) vertically
          centers this single line within a taller line box while staying
          in normal text flow — a flex container drops whitespace-only text
          nodes (the `{' '}` between words) entirely, per the flexbox spec,
          which is why an earlier h-9/flex version rendered "6deliverables"
          with no space. Insights-only, tied to insightsStats existing. */}
      {activeTab === 'insights' && insightsStats && (
        <p className="absolute left-12 top-2 z-20 hidden text-xs font-medium leading-7 text-zinc-400 sm:block">
          <span className="font-semibold text-zinc-200">
            {insightsStats.total}
          </span>{' '}
          deliverables ·{' '}
          <span className="font-semibold text-amber-400">
            {insightsStats.attentionCount}
          </span>{' '}
          need attention
          {/* Catalogue learnings hidden for now — not ready to surface yet. */}
        </p>
      )}

      <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden pt-9 sm:pt-10">
        {/* How this works has no home on mobile since the floating desktop
            copy above is sm:hidden — keep a trigger reachable here. */}
        <div className="mb-1 flex shrink-0 justify-end sm:hidden">
          <HowThisWorksPanel />
        </div>

        {/* Tab Content Container. No horizontal padding here — BaseLayout's
            own p-2/sm:p-4 on the app frame already insets this; adding
            px-1 on top of that was a second, redundant layer of left/right
            spacing.
            Every tab now manages its own internal scrolling — filter bar
            (or, for Insights, the toolbar) stays fixed and only the list
            below it scrolls — so this container must not *also* scroll, or
            the filter row would scroll away along with the list inside it. */}
        <div className="min-h-0 flex-1 overflow-hidden">
          {activeTab === 'insights' && (
            <InsightsGantt
              accessToken={projectToken}
              view={ganttView}
              zoom={ganttZoom}
              onViewChange={setGanttView}
              onZoomChange={setGanttZoom}
              onStatsChange={setInsightsStats}
            />
          )}
          {activeTab === 'commitments' && (
            <CommitmentsList accessToken={projectToken} />
          )}
          {activeTab === 'approvals' && (
            <ApprovalsList accessToken={projectToken} />
          )}
          {activeTab === 'dependencies' && (
            <ConcernList accessToken={projectToken} subtype="DEPENDENCIES" />
          )}
          {activeTab === 'risks' && (
            <ConcernList accessToken={projectToken} subtype="RISK" />
          )}
          {activeTab === 'sla' && <SlaList accessToken={projectToken} />}
          {activeTab === 'mail' && (
            <MailTriageList accessToken={projectToken} />
          )}
        </div>
      </div>
    </BaseLayout>
  );
}
